import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Trash2, 
  Edit2, 
  Loader2,
  X,
  Building2,
  Calendar,
  DollarSign,
  BarChart3,
  History,
  ArrowUpRight,
  ShieldCheck
} from 'lucide-react';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  where,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  getDoc,
  increment } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import HistoryNotesView from './HistoryNotesView';
import { notify } from '../services/notify';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import ConfirmModal from './ConfirmModal';
import { FixedAsset, AssetType } from '../types';
import { ensureAccountByCode, getAccountById } from '../services/accountingService';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');

const toNumber = (value: unknown, fallback = 0) => {
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const toDateInputValue = (value: unknown, fallback: string) => {
  if (!value) return fallback;

  if (typeof value === 'string') {
    const datePart = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (datePart) return datePart;
  }

  const date = value instanceof Date
    ? value
    : typeof value === 'object' && value !== null && 'toDate' in value
      ? (value as { toDate?: () => Date }).toDate?.()
      : new Date(String(value));

  return date && !Number.isNaN(date.getTime())
    ? date.toISOString().split('T')[0]
    : fallback;
};

const normalizeDepreciationMethod = (value: unknown) => {
  switch (String(value ?? '').toLowerCase().replace(/[_-]/g, ' ').trim()) {
    case 'straight line':
      return 'Straight Line';
    case 'declining balance':
      return 'Declining Balance';
    case 'full depreciation':
      return 'Full Depreciation';
    case 'none':
    case 'no depreciation':
      return 'None';
    default:
      return typeof value === 'string' && value ? value : 'Straight Line';
  }
};

const normalizeFixedAsset = (asset: any, index: number): FixedAsset => {
  let metadata: Record<string, any> = {};
  if (asset?.metadata && typeof asset.metadata === 'object') {
    metadata = asset.metadata;
  } else if (typeof asset?.metadata === 'string') {
    try {
      metadata = JSON.parse(asset.metadata);
    } catch {
      metadata = {};
    }
  }

  const data = { ...metadata, ...(asset ?? {}) };
  const today = new Date().toISOString().split('T')[0];
  const purchaseDate = toDateInputValue(
    data.purchaseDate ?? data.purchase_date ?? data.acquisitionDate ?? data.acquisition_date,
    today
  );
  const purchasePrice = toNumber(
    data.purchasePrice ?? data.purchase_price ?? data.acquisitionCost ?? data.acquisition_cost
  );
  const accumulatedDepreciation = toNumber(
    data.accumulatedDepreciation ?? data.accumulated_depreciation
  );
  const usefulLifeMonths = toNumber(
    data.usefulLifeMonths ?? data.useful_life_months,
    60
  );
  const effectiveLifeYears = toNumber(
    data.effectiveLifeYears ?? data.effective_life_years,
    usefulLifeMonths / 12 || 5
  );
  const rawBookValue = data.bookValue ?? data.book_value;
  const rawStatus = String(data.status ?? '').toLowerCase();
  const rawLastDepreciatedDate = data.lastDepreciatedDate ?? data.last_depreciated_date;

  return {
    id: String(data.id ?? data._id ?? data.assetId ?? data.asset_id ?? `local-asset-${index}`),
    assetNumber: String(data.assetNumber ?? data.asset_number ?? data.assetCode ?? data.asset_code ?? ''),
    assetName: String(data.assetName ?? data.asset_name ?? data.name ?? ''),
    purchaseDate,
    purchasePrice,
    assetType: String(data.assetType ?? data.asset_type ?? data.category ?? ''),
    store: data.store ?? data.store_name ?? '',
    classificationType: data.classificationType ?? data.classification_type ?? '',
    warrantyExpiry: data.warrantyExpiry ?? data.warranty_expiry ?? '',
    serialNumber: data.serialNumber ?? data.serial_number ?? '',
    description: data.description ?? '',
    depreciationStartDate: toDateInputValue(
      data.depreciationStartDate ?? data.depreciation_start_date,
      purchaseDate
    ),
    depreciationMethod: normalizeDepreciationMethod(
      data.depreciationMethod ?? data.depreciation_method
    ),
    averagingMethod: data.averagingMethod ?? data.averaging_method ?? 'The actual days',
    calculateDepreciationBasedOn: String(
      data.calculateDepreciationBasedOn ?? data.calculate_depreciation_based_on ?? ''
    ).toLowerCase().includes('life') ? 'Effective Life' : 'Rate',
    depreciationRate: toNumber(data.depreciationRate ?? data.depreciation_rate),
    effectiveLifeYears,
    costLimit: toNumber(data.costLimit ?? data.cost_limit),
    residualValue: toNumber(data.residualValue ?? data.residual_value),
    accumulatedDepreciation,
    bookValue: rawBookValue === undefined || rawBookValue === null || rawBookValue === ''
      ? purchasePrice - accumulatedDepreciation
      : toNumber(rawBookValue),
    status: rawStatus === 'draft'
      ? 'Draft'
      : rawStatus === 'disposed'
        ? 'Disposed'
        : 'Registered',
    lastDepreciatedDate: rawLastDepreciatedDate
      ? toDateInputValue(rawLastDepreciatedDate, purchaseDate)
      : undefined,
    usefulLifeMonths,
    attachments: Array.isArray(data.attachments)
      ? data.attachments.map((attachment: any) => ({
          name: String(attachment?.name ?? attachment?.fileName ?? ''),
          url: String(attachment?.url ?? attachment?.fileUrl ?? ''),
          type: String(attachment?.type ?? attachment?.mimeType ?? ''),
          size: attachment?.size === undefined ? undefined : toNumber(attachment.size)
        }))
      : [],
    createdBy: String(data.createdBy ?? data.created_by ?? ''),
    createdAt: (data.createdAt ?? data.created_at ?? new Date()) as FixedAsset['createdAt'],
    updatedAt: (data.updatedAt ?? data.updated_at) as FixedAsset['updatedAt']
  };
};

export default function FixedAssetsView({ orgId, language = 'en' }: { orgId: string, language?: string }) {
  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssetTypeModalOpen, setIsAssetTypeModalOpen] = useState(false);
  const [isDepreciationModalOpen, setIsDepreciationModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<FixedAsset | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [depreciationDate, setDepreciationDate] = useState(new Date().toISOString().split('T')[0]);
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const [trackingCategories, setTrackingCategories] = useState<any[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);

  const [newAssetType, setNewAssetType] = useState<Omit<AssetType, 'id' | 'createdAt'>>({
    orgId: '',
    name: '',
    assetAccount: '',
    accumulatedDepreciationAccount: '',
    depreciationAccount: '',
    depreciationMethod: 'Straight Line',
    averagingMethod: 'The actual days',
    calculateDepreciationBasedOn: 'Rate',
    depreciationRate: 0,
    effectiveLifeYears: 5,
    status: 'active'
  });

  useEffect(() => {
    if (!orgId) return;
    const appAuthToken = getLocalAuthToken();
    if (appAuthToken) {
      const controller = new AbortController();
      void fetch(`/api/organizations/${orgId}/asset-types`, {
        headers: { Authorization: `Bearer ${appAuthToken}` },
        signal: controller.signal
      })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load asset types: ${response.status}`);
          return response.json();
        })
        .then(payload => {
          if (!controller.signal.aborted) setAssetTypes(Array.isArray(payload) ? payload : []);
        })
        .catch(error => {
          if (!controller.signal.aborted) {
            console.error('Local API error loading asset types:', error);
            setAssetTypes([]);
          }
        });
      return () => controller.abort();
    }

    const q = query(
      collection(db, 'asset_types'),
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAssetTypes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as AssetType[]);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'asset_types');
    });
    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const appAuthToken = getLocalAuthToken();
    if (appAuthToken) {
      const controller = new AbortController();
      void fetch(`/api/organizations/${orgId}/chart-of-accounts`, {
        headers: { Authorization: `Bearer ${appAuthToken}` },
        signal: controller.signal
      })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load accounts: ${response.status}`);
          return response.json();
        })
        .then(payload => {
          if (!controller.signal.aborted) setAccounts(Array.isArray(payload) ? payload : []);
        })
        .catch(error => {
          if (!controller.signal.aborted) {
            console.error('Local API error loading accounts:', error);
            setAccounts([]);
          }
        });
      return () => controller.abort();
    }

    const q = query(collection(db, 'accounts'), where('orgId', '==', orgId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'accounts');
    });
    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    if (getLocalAuthToken()) {
      // Tracking categories are not a local data model yet. Keep the optional
      // store selector empty rather than opening an unauthorized Firestore listener.
      setTrackingCategories([]);
      return;
    }

    const trackingQ = query(
      collection(db, 'tracking_categories'),
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubscribe = onSnapshot(trackingQ, (snapshot) => {
      setTrackingCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tracking_categories');
    });
    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const appAuthToken = getLocalAuthToken();
    if (appAuthToken) {
      const controller = new AbortController();
      void fetch(`/api/organizations/${orgId}/settings`, {
        headers: { Authorization: `Bearer ${appAuthToken}` },
        signal: controller.signal
      })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load settings: ${response.status}`);
          return response.json();
        })
        .then(payload => {
          if (!controller.signal.aborted) setOrgSettings({ ...(payload?.data || {}), ...(payload || {}) });
        })
        .catch(error => {
          if (!controller.signal.aborted) console.error('Local API error loading settings:', error);
        });
      return () => controller.abort();
    }

    const settingsRef = doc(db, 'settings', orgId);
    const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setOrgSettings(snapshot.data());
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'settings/' + orgId);
    });
    return () => unsubscribe();
  }, [orgId]);

  const [newAsset, setNewAsset] = useState<Omit<FixedAsset, 'id' | 'createdAt' | 'createdBy'>>({
    assetNumber: '',
    assetName: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchasePrice: 0,
    assetType: 'Equipment',
    store: '',
    classificationType: '',
    warrantyExpiry: '',
    serialNumber: '',
    description: '',
    depreciationStartDate: new Date().toISOString().split('T')[0],
    depreciationMethod: 'Straight Line',
    averagingMethod: 'The actual days',
    calculateDepreciationBasedOn: 'Rate',
    depreciationRate: 0,
    effectiveLifeYears: 5,
    costLimit: 0,
    residualValue: 0,
    accumulatedDepreciation: 0,
    bookValue: 0,
    status: 'Draft',
    usefulLifeMonths: 60,
    attachments: []
  });

  const getPendingDepreciation = () => {
    // Assets that are Registered and haven't been depreciated up to/in the selected period
    // Simple logic: if lastDepreciatedDate is before depreciationDate (comparing YYYY-MM)
    return assets.filter(asset => {
      if (asset.status !== 'Registered') return false;
      if (asset.depreciationMethod === 'None') return false;
      if (asset.bookValue <= 0) return false;
      
      const lastDep = asset.lastDepreciatedDate || asset.purchaseDate;
      const lastDepDate = new Date(lastDep);
      const targetDate = new Date(depreciationDate);
      
      // Return true if last depreciation was in a previous month
      return (targetDate.getFullYear() > lastDepDate.getFullYear() || 
             (targetDate.getFullYear() === lastDepDate.getFullYear() && targetDate.getMonth() > lastDepDate.getMonth()));
    });
  };

  const calculateDepreciation = (asset: FixedAsset) => {
    const lastDep = asset.lastDepreciatedDate || asset.purchaseDate;
    const lastDepDate = new Date(lastDep);
    const targetDate = new Date(depreciationDate);
    
    // Calculate months difference
    const diffMonths = (targetDate.getFullYear() - lastDepDate.getFullYear()) * 12 + 
                      (targetDate.getMonth() - lastDepDate.getMonth());
    
    if (diffMonths <= 0) return 0;

    let amount = 0;
    if (asset.depreciationMethod === 'Straight Line') {
      // Annual Rate / 12 * months
      amount = (asset.purchasePrice * (asset.depreciationRate / 100) / 12) * diffMonths;
    } else if (asset.depreciationMethod === 'Declining Balance') {
      // Very simplified DB monthly calculation
      // For each month, take rate/12 of current book value
      let currentBookValue = asset.bookValue;
      for (let i = 0; i < diffMonths; i++) {
        const monthDep = currentBookValue * (asset.depreciationRate / 100) / 12;
        amount += monthDep;
        currentBookValue -= monthDep;
      }
    }
    
    // Don't depreciate below zero
    return Math.min(amount, asset.bookValue);
  };

  useEffect(() => {
    if (!orgId) return;

    const appAuthToken = getLocalAuthToken();
    if (appAuthToken) {
      const controller = new AbortController();

      const loadLocalAssets = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(`/api/organizations/${orgId}/fixed-assets`, {
            headers: { Authorization: `Bearer ${appAuthToken}` },
            signal: controller.signal
          });
          if (!response.ok) {
            throw new Error(`Failed to load fixed assets: ${response.status}`);
          }

          const payload = await response.json();
          const rawAssets = Array.isArray(payload)
            ? payload
            : payload.fixedAssets ?? payload.fixed_assets ?? payload.assets ?? payload.data ?? [];

          if (!controller.signal.aborted) {
            setAssets(
              (Array.isArray(rawAssets) ? rawAssets : []).map(normalizeFixedAsset)
            );
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Local API error loading fixed assets:', error);
            setAssets([]);
          }
        } finally {
          if (!controller.signal.aborted) {
            setIsLoading(false);
          }
        }
      };

      void loadLocalAssets();
      return () => controller.abort();
    }

    const q = query(
      collection(db, 'fixed_assets'),
      where('orgId', '==', orgId),
      orderBy('assetNumber', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FixedAsset[];
      setAssets(data);
      setIsLoading(false);
    }, (error) => {
      setIsLoading(false);
      handleFirestoreError(error, OperationType.LIST, 'fixed_assets');
    });

    return () => unsubscribe();
  }, [orgId]);

  const handleSaveAsset = async (e: React.FormEvent, status: 'Draft' | 'Registered') => {
    if (e) e.preventDefault();
    setIsSaving(true);
    try {
      const appAuthToken = getLocalAuthToken();
      if (appAuthToken) {
        const bookValue = newAsset.purchasePrice - newAsset.accumulatedDepreciation;
        const localAssetData = {
          ...newAsset,
          status,
          bookValue,
          name: newAsset.assetName,
          assetCode: newAsset.assetNumber,
          category: newAsset.assetType,
          acquisitionDate: newAsset.purchaseDate,
          acquisitionCost: newAsset.purchasePrice,
          usefulLifeMonths: newAsset.usefulLifeMonths ?? (newAsset.effectiveLifeYears ? newAsset.effectiveLifeYears * 12 : null),
          residualValue: newAsset.residualValue ?? 0,
          depreciationMethod: newAsset.depreciationMethod
        };
        const isUpdating = isEditing && selectedAsset;
        const response = await fetch(
          isUpdating
            ? `/api/organizations/${orgId}/fixed-assets/${selectedAsset.id}`
            : `/api/organizations/${orgId}/fixed-assets`,
          {
            method: isUpdating ? 'PATCH' : 'POST',
            headers: {
              Authorization: `Bearer ${appAuthToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(localAssetData)
          }
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to save fixed asset: ${response.status}`);
        }

        const rawAsset = payload?.fixedAsset ?? payload?.fixed_asset ?? payload?.asset ?? payload?.data ?? payload;
        const normalizedAsset = normalizeFixedAsset(rawAsset, assets.length);
        setAssets(currentAssets => isUpdating
          ? currentAssets.map(asset => asset.id === selectedAsset.id ? normalizedAsset : asset)
          : [...currentAssets, normalizedAsset]
        );
        if (!isUpdating) {
          setOrgSettings((current: any) => ({
            ...(current || {}),
            nextFixedAssetsNumber: Number(current?.nextFixedAssetsNumber || 1) + 1
          }));
        }
      } else {
        const assetData = {
          ...newAsset,
          status,
          orgId,
          bookValue: newAsset.purchasePrice - newAsset.accumulatedDepreciation,
          updatedAt: serverTimestamp()
        };

        if (isEditing && selectedAsset) {
          await updateDoc(doc(db, 'fixed_assets', selectedAsset.id), assetData);
        } else {
          await addDoc(collection(db, 'fixed_assets'), {
            ...assetData,
            createdBy: auth.currentUser?.uid,
            createdAt: serverTimestamp()
          });

          // Increment settings number
          if (orgId) {
            try {
              await updateDoc(doc(db, 'settings', orgId), {
                nextFixedAssetsNumber: increment(1)
              });
            } catch (err) {
              console.error("Error incrementing asset number:", err);
            }
          }
        }
      }
      setIsModalOpen(false);
      setIsEditing(false);
      notify.confirm(isEditing ? "Asset updated" : "Asset created");
    } catch (error) {
      if (getLocalAuthToken()) {
        console.error('Local API error saving fixed asset:', error);
      } else {
        handleFirestoreError(error, OperationType.WRITE, 'fixed_assets');
      }
      notify.error(error instanceof Error ? error.message : "Failed to save asset");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAsset = async (id: string) => {
    try {
      const appAuthToken = getLocalAuthToken();
      if (appAuthToken) {
        const response = await fetch(`/api/organizations/${orgId}/fixed-assets/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${appAuthToken}` }
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || `Failed to delete fixed asset: ${response.status}`);
        setAssets(currentAssets => currentAssets.filter(asset => asset.id !== id));
        if (selectedAsset?.id === id) {
          setSelectedAsset(null);
          setIsViewModalOpen(false);
        }
      } else {
        await deleteDoc(doc(db, 'fixed_assets', id));
      }
      notify.confirm("Asset deleted");
      setDeleteId(null);
    } catch (error) {
      if (getLocalAuthToken()) {
        console.error('Local API error deleting fixed asset:', error);
      } else {
        handleFirestoreError(error, OperationType.DELETE, 'fixed_assets/' + id);
      }
      notify.error(error instanceof Error ? error.message : "Failed to delete asset");
    }
  };

  const handlePostDepreciation = async () => {
    const pending = getPendingDepreciation();
    if (pending.length === 0) {
      notify.error("No assets pending depreciation for this period");
      return;
    }

    setIsSaving(true);
    try {
      const appAuthToken = getLocalAuthToken();
      if (appAuthToken) {
        const response = await fetch(`/api/organizations/${orgId}/fixed-assets/depreciation-runs`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ depreciationDate })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || `Failed to post depreciation: ${response.status}`);
        const updatedAssets = new Map<string, FixedAsset>(
          (Array.isArray(payload?.assets) ? payload.assets : [])
            .map((asset: any, index: number): [string, FixedAsset] => {
              const normalized = normalizeFixedAsset(asset, index);
              return [normalized.id, normalized];
            })
        );
        setAssets(currentAssets => currentAssets.map(asset => updatedAssets.get(asset.id) || asset));
        notify.confirm(`Depreciation posted for ${updatedAssets.size} assets`);
      } else {
        // Resolve depreciation accounts. Prefer the asset type's configured accounts,
        // otherwise fall back to the standard COA codes (SYSTEM_DOCUMENTATION §9.2):
        //   Debit  Depreciation Expense (6050)
        //   Credit Accumulated Depreciation (1590 in the seeded COA)
        const resolveAccount = async (accountId: string | undefined, fallbackCode: string, fallbackData: any) => {
          if (accountId) {
            const byId = await getAccountById(accountId);
            if (byId) return byId;
          }
          return ensureAccountByCode(orgId, fallbackCode, fallbackData, auth.currentUser?.uid || 'system');
        };

        let totalDepreciation = 0;
        const batchUpdate = pending.map(async (asset) => {
          const depAmount = calculateDepreciation(asset);
          totalDepreciation += depAmount;
          const newAccumulated = asset.accumulatedDepreciation + depAmount;
          const newBookValue = asset.purchasePrice - newAccumulated;
          
          return updateDoc(doc(db, 'fixed_assets', asset.id!), {
            accumulatedDepreciation: newAccumulated,
            bookValue: newBookValue,
            lastDepreciatedDate: depreciationDate,
            updatedAt: serverTimestamp()
          });
        });

        await Promise.all(batchUpdate);

        // Post the depreciation GL journal for the total amount.
        if (totalDepreciation > 0) {
          const firstType = assetTypes.find(t => pending.some(a => a.assetType === t.name));
          const expenseAccount = await resolveAccount(firstType?.depreciationAccount, '6050', { name: 'Depreciation Expense', type: 'Expense', nature: 'Debit', taxRate: '0%' });
          const accumulatedAccount = await resolveAccount(firstType?.accumulatedDepreciationAccount, '1590', { name: 'Accumulated Depreciation', type: 'Fixed Asset', nature: 'Credit', taxRate: '0%' });

          await addDoc(collection(db, 'manual_journals'), {
            orgId,
            date: depreciationDate,
            narration: `Monthly Depreciation - ${depreciationDate}`,
            reference: `DEP-${new Date(depreciationDate).toISOString().slice(0, 7)}`,
            status: 'Posted',
            items: [
              { accountId: expenseAccount.id, description: 'Depreciation Expense', debit: totalDepreciation, credit: 0 },
              { accountId: accumulatedAccount.id, description: 'Accumulated Depreciation', debit: 0, credit: totalDepreciation }
            ],
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser?.uid,
            totalDebit: totalDepreciation,
            totalCredit: totalDepreciation,
          });
        }

        notify.confirm(`Depreciation posted for ${pending.length} assets`);
      }
      setIsDepreciationModalOpen(false);
    } catch (error) {
      if (getLocalAuthToken()) {
        console.error('Local API error posting depreciation:', error);
      } else {
        handleFirestoreError(error, OperationType.WRITE, 'fixed_assets (batch)');
      }
      notify.error(error instanceof Error ? error.message : "Failed to post depreciation");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAssetType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setIsSaving(true);
    try {
      const appAuthToken = getLocalAuthToken();
      if (appAuthToken) {
        const response = await fetch(`/api/organizations/${orgId}/asset-types`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newAssetType)
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || `Failed to save asset type: ${response.status}`);
        setAssetTypes(current => [...current, payload as AssetType].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        await addDoc(collection(db, 'asset_types'), {
          ...newAssetType,
          orgId,
          createdAt: serverTimestamp()
        });
      }
      setIsAssetTypeModalOpen(false);
      setNewAssetType({
        orgId: '',
        name: '',
        assetAccount: '',
        accumulatedDepreciationAccount: '',
        depreciationAccount: '',
        depreciationMethod: 'Straight Line',
        averagingMethod: 'The actual days',
        calculateDepreciationBasedOn: 'Rate',
        depreciationRate: 0,
        effectiveLifeYears: 5,
        status: 'active'
      });
      notify.confirm("Asset type created");
    } catch (error) {
      if (getLocalAuthToken()) {
        console.error('Local API error saving asset type:', error);
      } else {
        handleFirestoreError(error, OperationType.CREATE, 'asset_types');
      }
      notify.error(error instanceof Error ? error.message : "Failed to save asset type");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssetTypeChange = (typeName: string) => {
    const type = assetTypes.find(t => t.name === typeName);
    if (type) {
      setNewAsset({
        ...newAsset,
        assetType: typeName,
        depreciationMethod: type.depreciationMethod,
        averagingMethod: type.averagingMethod || 'The actual days',
        calculateDepreciationBasedOn: type.calculateDepreciationBasedOn,
        depreciationRate: type.depreciationRate || 0,
        effectiveLifeYears: type.effectiveLifeYears || 5
      });
    } else {
      setNewAsset({ ...newAsset, assetType: typeName });
    }
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.assetName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         asset.assetNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || asset.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setNewAsset(prev => ({
          ...prev,
          attachments: [
            ...(prev.attachments || []),
            {
              name: file.name,
              url: base64String,
              type: file.type,
              size: file.size
            }
          ]
        }));
      };
      reader.readAsDataURL(file);
    });
    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setNewAsset(prev => ({
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fixed Assets</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Track and manage your organization's long-term assets</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsDepreciationModalOpen(true)}
            className="flex items-center gap-2 bg-white border border-[#141414] text-[#141414] px-6 py-3 rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold"
          >
            <BarChart3 className="w-4 h-4" />
            <span>Run Depreciation</span>
          </button>
          <button 
            onClick={() => {
              setIsEditing(false);
              const prefix = orgSettings?.fixedAssetsPrefix || 'FA-';
              const nextNum = orgSettings?.nextFixedAssetsNumber || 1;
              setNewAsset({
                assetNumber: `${prefix}${nextNum}`,
                assetName: '',
                purchaseDate: new Date().toISOString().split('T')[0],
                purchasePrice: 0,
                assetType: 'Computer Equipment',
                store: '',
                classificationType: '',
                warrantyExpiry: '',
                serialNumber: '',
                description: '',
                depreciationStartDate: new Date().toISOString().split('T')[0],
                depreciationMethod: 'Straight Line',
                averagingMethod: 'The actual days',
                calculateDepreciationBasedOn: 'Rate',
                depreciationRate: 0,
                effectiveLifeYears: 5,
                costLimit: 0,
                residualValue: 0,
                accumulatedDepreciation: 0,
                bookValue: 0,
                status: 'Draft',
                usefulLifeMonths: 60,
                attachments: []
              });
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
          >
            <Plus className="w-4 h-4" />
            <span>New Asset</span>
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
          {['All', 'Draft', 'Registered', 'Disposed'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={cn(
                "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border",
                statusFilter === status 
                  ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                  : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
              )}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input 
            type="text" 
            placeholder="Search assets..." 
            className="w-full pl-10 pr-4 py-2 bg-white/50 border border-[#141414]/10 rounded-full outline-none focus:border-[#141414] transition-all text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading assets...</p>
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <Building2 className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No assets found</p>
          <p className="text-sm opacity-30 mt-2">Add your first fixed asset to start tracking depreciation</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.map((asset) => (
            <div 
              key={asset.id}
              className="bg-white/50 border border-[#141414] rounded-3xl p-6 hover:shadow-xl transition-all group relative overflow-hidden cursor-pointer"
              onClick={() => {
                setSelectedAsset(asset);
                setIsViewModalOpen(true);
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#141414] text-[#E4E3E0] rounded-2xl flex items-center justify-center font-bold text-xl">
                  {asset.assetName.charAt(0)}
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "px-3 py-1 rounded-full text-[10px] font-bold border",
                    asset.status === 'Registered' ? "bg-green-100 text-green-700 border-green-200" : 
                    asset.status === 'Draft' ? "bg-blue-100 text-blue-700 border-blue-200" :
                    "bg-gray-100 text-gray-700 border-gray-200"
                  )}>
                    {asset.status}
                  </span>
                </div>
              </div>

              <div className="space-y-1 mb-6">
                <h3 className="text-lg font-bold tracking-tight truncate group-hover:text-orange-600 transition-colors">{asset.assetName}</h3>
                <p className="text-xs opacity-50 font-mono">{asset.assetNumber} • {asset.assetType}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm opacity-70">
                  <Calendar className="w-4 h-4" />
                  <span>Purchased: {asset.purchaseDate}</span>
                </div>
                <div className="flex items-center gap-3 text-sm opacity-70">
                  <DollarSign className="w-4 h-4" />
                  <span>Cost: ${asset.purchasePrice.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-3 text-sm opacity-70">
                  <BarChart3 className="w-4 h-4" />
                  <span>Book Value: ${asset.bookValue.toLocaleString()}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-[#141414]/5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-[10px] uppercase font-bold opacity-40">Depreciation</p>
                    <p className="text-sm font-bold">{asset.depreciationRate}%</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAsset(asset);
                      setNewAsset({ ...asset });
                      setIsEditing(true);
                      setIsModalOpen(true);
                    }}
                    className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(asset.id!);
                    }}
                    className="p-2 hover:bg-red-500 hover:text-white rounded-full transition-colors text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Asset Modal */}
      {isViewModalOpen && selectedAsset && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsViewModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-6 border-b border-[#141414] flex items-center justify-between bg-[#141414] text-[#E4E3E0]">
              <div className="flex items-center gap-3">
                <Building2 className="w-6 h-6" />
                <div>
                  <h2 className="text-xl font-bold italic serif">{selectedAsset.assetName}</h2>
                  <p className="text-[10px] uppercase tracking-widest opacity-60">{selectedAsset.assetNumber}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsViewModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">Asset Details</label>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Type</span>
                        <span className="font-bold">{selectedAsset.assetType}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Stores</span>
                        <span className="font-bold">{selectedAsset.store || '—'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Serial Number</span>
                        <span className="font-bold">{selectedAsset.serialNumber || '—'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Status</span>
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tighter",
                          selectedAsset.status === 'Registered' ? "bg-green-100 text-green-700" : 
                          selectedAsset.status === 'Draft' ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-700"
                        )}>{selectedAsset.status}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">Purchase Information</label>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Date</span>
                        <span className="font-bold">{new Date(selectedAsset.purchaseDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Price</span>
                        <span className="font-bold font-mono">${selectedAsset.purchasePrice.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Warranty Expiry</span>
                        <span className="font-bold">{selectedAsset.warrantyExpiry ? new Date(selectedAsset.warrantyExpiry).toLocaleDateString() : '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">Depreciation</label>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Method</span>
                        <span className="font-bold">{selectedAsset.depreciationMethod}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Rate</span>
                        <span className="font-bold">{selectedAsset.depreciationRate}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Last Depreciated</span>
                        <span className="font-bold">{selectedAsset.lastDepreciatedDate ? new Date(selectedAsset.lastDepreciatedDate).toLocaleDateString() : 'Never'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">Valuation</label>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-60 italic serif">Accumulated Dep.</span>
                        <span className="font-bold font-mono text-red-600">-${selectedAsset.accumulatedDepreciation.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-[#141414]/10">
                        <span className="opacity-60 italic serif">Book Value</span>
                        <span className="font-bold font-mono text-lg">${selectedAsset.bookValue.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-1">Description</label>
                    <p className="text-sm opacity-70 leading-relaxed italic serif">{selectedAsset.description || 'No description provided.'}</p>
                  </div>
                  
                  {selectedAsset.attachments && selectedAsset.attachments.length > 0 && (
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block mb-2">Attachments</label>
                      <div className="grid grid-cols-1 gap-2">
                        {(selectedAsset.attachments || []).map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white border border-[#141414]/10 rounded-xl">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#141414] text-[#E4E3E0] rounded-lg flex items-center justify-center text-[10px] font-bold">
                                {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                              </div>
                              <span className="text-xs font-bold truncate max-w-[200px]">{file.name}</span>
                            </div>
                            <a 
                              href={file.url} 
                              download={file.name}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[10px] font-bold uppercase tracking-widest text-blue-600 hover:underline"
                            >
                              Download
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-8 border-t border-[#141414]">
                <HistoryNotesView orgId={orgId} entityId={selectedAsset.id!} entityType="FixedAsset" />
              </div>
            </div>
            
            <div className="p-6 bg-[#141414]/5 border-t border-[#141414] flex justify-end gap-3">
              <button 
                onClick={() => {
                  setIsViewModalOpen(false);
                  setIsEditing(true);
                  setNewAsset({
                    assetNumber: selectedAsset.assetNumber,
                    assetName: selectedAsset.assetName,
                    purchaseDate: selectedAsset.purchaseDate,
                    purchasePrice: selectedAsset.purchasePrice,
                    assetType: selectedAsset.assetType,
                    store: selectedAsset.store || '',
                    classificationType: selectedAsset.classificationType || '',
                    warrantyExpiry: selectedAsset.warrantyExpiry || '',
                    serialNumber: selectedAsset.serialNumber || '',
                    description: selectedAsset.description || '',
                    depreciationStartDate: selectedAsset.depreciationStartDate || selectedAsset.purchaseDate,
                    depreciationMethod: selectedAsset.depreciationMethod,
                    averagingMethod: selectedAsset.averagingMethod || 'The actual days',
                    calculateDepreciationBasedOn: selectedAsset.calculateDepreciationBasedOn || 'Rate',
                    depreciationRate: selectedAsset.depreciationRate,
                    effectiveLifeYears: selectedAsset.effectiveLifeYears || 5,
                    costLimit: selectedAsset.costLimit || 0,
                    residualValue: selectedAsset.residualValue || 0,
                    accumulatedDepreciation: selectedAsset.accumulatedDepreciation,
                    bookValue: selectedAsset.bookValue,
                    status: selectedAsset.status,
                    usefulLifeMonths: selectedAsset.usefulLifeMonths || 60,
                    attachments: selectedAsset.attachments || []
                  });
                  setIsModalOpen(true);
                }}
                className="px-6 py-2 bg-white border border-[#141414] rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
              >
                Edit Asset
              </button>
              <button 
                onClick={() => setIsViewModalOpen(false)}
                className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-xl text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Depreciation Modal */}
      {isDepreciationModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsDepreciationModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-5xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-6 border-b border-[#141414] flex items-center justify-between bg-[#141414] text-[#E4E3E0]">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-6 h-6" />
                <div>
                  <h2 className="text-xl font-bold italic serif">Run Depreciation</h2>
                  <p className="text-[10px] uppercase tracking-widest opacity-60">Register depreciation for a specific period</p>
                </div>
              </div>
              <button 
                onClick={() => setIsDepreciationModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-8 flex-1 overflow-y-auto custom-scrollbar">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 bg-white/50 border border-[#141414] p-6 rounded-2xl">
                <div className="space-y-4 flex-1">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Depreciation Period (Up To)</label>
                    <input 
                      type="date" 
                      className="w-full max-w-xs bg-white border border-[#141414] p-3 rounded-xl outline-none font-bold"
                      value={depreciationDate}
                      onChange={(e) => setDepreciationDate(e.target.value)}
                    />
                  </div>
                  <p className="text-xs opacity-60 italic serif max-w-md">
                    This will calculate and post depreciation for all registered assets from their last depreciation date up to the selected date.
                  </p>
                </div>
                
                <div className="flex gap-8">
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Assets Pending</p>
                    <p className="text-2xl font-bold">{getPendingDepreciation().length}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Total Depreciation</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${getPendingDepreciation().reduce((sum, asset) => sum + calculateDepreciation(asset), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Assets Summary</h3>
                <div className="border border-[#141414] rounded-2xl overflow-hidden bg-white/30">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[#141414] text-[#E4E3E0] text-[10px] uppercase tracking-widest font-bold">
                        <th className="p-4">Asset</th>
                        <th className="p-4">Method</th>
                        <th className="p-4">Current Book Value</th>
                        <th className="p-4">Last Depreciated</th>
                        <th className="p-4 text-right">To Depreciate</th>
                        <th className="p-4 text-right">New Book Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#141414]/10">
                      {getPendingDepreciation().length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-12 text-center italic serif opacity-50">
                            No assets found pending depreciation for this period.
                          </td>
                        </tr>
                      ) : (
                        getPendingDepreciation().map(asset => {
                          const depAmount = calculateDepreciation(asset);
                          const newBookValue = asset.bookValue - depAmount;
                          return (
                            <tr key={asset.id} className="text-sm hover:bg-white transition-colors">
                              <td className="p-4">
                                <p className="font-bold">{asset.assetName}</p>
                                <p className="text-[10px] opacity-40 font-mono">{asset.assetNumber}</p>
                              </td>
                              <td className="p-4 opacity-70">{asset.depreciationMethod} ({asset.depreciationRate}%)</td>
                              <td className="p-4 font-mono font-bold">${asset.bookValue.toLocaleString()}</td>
                              <td className="p-4 opacity-70 italic serif">{asset.lastDepreciatedDate ? new Date(asset.lastDepreciatedDate).toLocaleDateString() : 'Never'}</td>
                              <td className="p-4 text-right font-bold text-red-600 font-mono">-${depAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="p-4 text-right font-bold font-mono text-lg">${newBookValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="p-8 border-t border-[#141414] bg-white flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm italic serif opacity-50">
                <ArrowUpRight className="w-4 h-4" />
                <p>Ensure your assets are reviewed before posting. This action updates the fixed assets register.</p>
              </div>
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setIsDepreciationModalOpen(false)}
                  className="px-8 py-3 rounded-full font-bold hover:bg-[#141414]/5 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handlePostDepreciation}
                  disabled={isSaving || getPendingDepreciation().length === 0}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-3 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-30"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>Post Depreciation</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Asset Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-5xl max-h-[calc(100dvh-2rem)] rounded-xl overflow-hidden flex flex-col shadow-2xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {isEditing ? `Edit asset: ${newAsset.assetName}` : 'New asset'}
              </h2>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={(e) => handleSaveAsset(e, 'Registered')} className="flex-1 overflow-y-auto p-6 space-y-8 bg-[#F8F9FA] custom-scrollbar">
              {/* Details Section */}
              <div className="bg-white border rounded-lg shadow-sm">
                <div className="p-4 border-b flex items-center justify-between">
                   <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-800">Details</h3>
                   </div>
                   <div>
                      <input 
                        type="file" 
                        id="asset-file-upload" 
                        className="hidden" 
                        multiple 
                        accept="image/*,.pdf"
                        onChange={handleFileUpload}
                      />
                      <button 
                        type="button" 
                        onClick={() => document.getElementById('asset-file-upload')?.click()}
                        className="text-xs border px-3 py-1.5 rounded bg-white hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                      >
                        <Plus className="w-3 h-3" />
                        Attach file
                      </button>
                    </div>
                </div>
                <div className="p-6 space-y-6">
                  {newAsset.attachments && newAsset.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(newAsset.attachments || []).map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg border text-xs">
                          <span className="max-w-[150px] truncate">{file.name}</span>
                          <button 
                            type="button" 
                            onClick={() => removeAttachment(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-3 space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Asset name</label>
                      <input 
                        required
                        type="text" 
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 transition-all"
                        value={newAsset.assetName}
                        onChange={(e) => setNewAsset({...newAsset, assetName: e.target.value})}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Asset number</label>
                      <div className="relative">
                        <input 
                          required
                          type="text" 
                          className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 transition-all pr-8"
                          value={newAsset.assetNumber}
                          onChange={(e) => setNewAsset({...newAsset, assetNumber: e.target.value})}
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L5 5L9 1" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Purchase date</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input 
                          type="date" 
                          className="w-full border p-2 pl-10 rounded outline-none focus:ring-1 focus:ring-blue-500"
                          value={newAsset.purchaseDate}
                          onChange={(e) => setNewAsset({...newAsset, purchaseDate: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Purchase price</label>
                      <input 
                        required
                        type="number" 
                        step="0.01"
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                        value={newAsset.purchasePrice || ''}
                        onChange={(e) => setNewAsset({...newAsset, purchasePrice: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5 text-right flex flex-col justify-end">
                    <button 
                      type="button"
                      onClick={() => setIsAssetTypeModalOpen(true)}
                      className="text-xs text-blue-600 hover:underline font-semibold"
                    >
                      + Add new asset type
                    </button>
                    <label className="text-xs font-semibold text-gray-700 block text-left">Asset type</label>
                    <select 
                      className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white"
                      value={newAsset.assetType}
                      onChange={(e) => handleAssetTypeChange(e.target.value)}
                    >
                      <option value="">Select an asset type</option>
                      {assetTypes.map(type => (
                        <option key={type.id} value={type.name}>{type.name}</option>
                      ))}
                      <option value="Computer Equipment">Computer Equipment</option>
                      <option value="Medical Equipment">Medical Equipment</option>
                      <option value="Office Equipment">Office Equipment</option>
                      <option value="Furniture">Furniture</option>
                      <option value="Vehicles">Vehicles</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Warranty expiry</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input 
                          type="date" 
                          className="w-full border p-2 pl-10 rounded outline-none focus:ring-1 focus:ring-blue-500"
                          value={newAsset.warrantyExpiry}
                          onChange={(e) => setNewAsset({...newAsset, warrantyExpiry: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Serial number</label>
                      <input 
                        type="text" 
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                        value={newAsset.serialNumber}
                        onChange={(e) => setNewAsset({...newAsset, serialNumber: e.target.value})}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Stores</label>
                      <select 
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white"
                        value={newAsset.store}
                        onChange={(e) => setNewAsset({...newAsset, store: e.target.value})}
                      >
                        <option value="">Select a store</option>
                        {trackingCategories.find(c => c.name === 'Store' || c.name === 'Stores')?.options?.map((opt: any, idx: number) => (
                          <option key={`${opt.id || opt.name || idx}-${idx}`} value={opt.name || opt}>{opt.name || opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Type</label>
                      <select 
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white"
                        value={newAsset.classificationType}
                        onChange={(e) => setNewAsset({...newAsset, classificationType: e.target.value})}
                      >
                        <option value="">Select a type</option>
                        <option value="Fixed">Fixed</option>
                        <option value="Intangible">Intangible</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700">Description</label>
                    <textarea 
                      rows={3}
                      className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                      value={newAsset.description}
                      onChange={(e) => setNewAsset({...newAsset, description: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Book depreciation settings Section */}
              <div className="bg-white border rounded-lg shadow-sm">
                <div className="p-4 border-b">
                  <h3 className="font-bold text-gray-800">Book depreciation settings</h3>
                </div>
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Depreciation start date</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input 
                          type="date" 
                          className="w-full border p-2 pl-10 rounded outline-none focus:ring-1 focus:ring-blue-500"
                          value={newAsset.depreciationStartDate}
                          onChange={(e) => setNewAsset({...newAsset, depreciationStartDate: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Depreciation method</label>
                      <select 
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white"
                        value={newAsset.depreciationMethod}
                        onChange={(e) => setNewAsset({...newAsset, depreciationMethod: e.target.value})}
                      >
                        <option value="">Select a depreciation method</option>
                        <option value="Straight Line">Straight Line</option>
                        <option value="Declining Balance">Declining Balance</option>
                        <option value="Full Depreciation">Full Depreciation</option>
                        <option value="None">No Depreciation</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Averaging method</label>
                      <select 
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white"
                        value={newAsset.averagingMethod}
                        onChange={(e) => setNewAsset({...newAsset, averagingMethod: e.target.value})}
                      >
                        <option value="The actual days">The actual days</option>
                        <option value="Full month">Full month</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-xs font-semibold text-gray-700">Calculate depreciation based on</label>
                    <div className="flex items-center gap-8 p-4 border rounded bg-white">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="radio" 
                          name="depMode" 
                          className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                          checked={newAsset.calculateDepreciationBasedOn === 'Rate'}
                          onChange={() => setNewAsset({...newAsset, calculateDepreciationBasedOn: 'Rate'})}
                        />
                        <span className="text-sm font-medium text-gray-500 group-hover:text-gray-800 transition-colors">Rate</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="radio" 
                          name="depMode" 
                          className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                          checked={newAsset.calculateDepreciationBasedOn === 'Effective Life'}
                          onChange={() => setNewAsset({...newAsset, calculateDepreciationBasedOn: 'Effective Life'})}
                        />
                        <span className="text-sm font-medium text-gray-500 group-hover:text-gray-800 transition-colors">Effective life</span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {newAsset.calculateDepreciationBasedOn === 'Rate' ? (
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700">Depreciation rate (%)</label>
                        <input 
                          type="number" 
                          step="0.01"
                          className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                          value={newAsset.depreciationRate || ''}
                          onChange={(e) => setNewAsset({...newAsset, depreciationRate: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700">Effective life (Years)</label>
                        <input 
                          type="number" 
                          className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                          value={newAsset.effectiveLifeYears || ''}
                          onChange={(e) => setNewAsset({...newAsset, effectiveLifeYears: parseInt(e.target.value) || 0})}
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Cost limit</label>
                      <input 
                        type="number" 
                        step="0.01"
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                        value={newAsset.costLimit || ''}
                        onChange={(e) => setNewAsset({...newAsset, costLimit: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700">Residual value</label>
                      <input 
                        type="number" 
                        step="0.01"
                        className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                        value={newAsset.residualValue || ''}
                        onChange={(e) => setNewAsset({...newAsset, residualValue: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-x-3 pt-6">
                <button 
                  type="button"
                  onClick={(e) => handleSaveAsset(e as any, 'Draft')}
                  className="px-6 py-2.5 rounded-lg font-semibold border bg-white hover:bg-gray-50 transition-colors"
                >
                  Save as draft
                </button>
                <button 
                  type="button"
                  onClick={(e) => handleSaveAsset(e as any, 'Registered')}
                  disabled={isSaving}
                  className="bg-[#0073C0] text-white px-8 py-2.5 rounded-lg font-semibold hover:bg-[#005FA0] transition-colors flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>Register</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Asset Type Modal */}
      {isAssetTypeModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-black/50 backdrop-blur-sm z-[150] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsAssetTypeModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-xl flex flex-col shadow-2xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">New asset type</h2>
              <button 
                onClick={() => setIsAssetTypeModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveAssetType} className="p-6 space-y-6">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700">Account type (Name)</label>
                <input 
                  required
                  type="text" 
                  className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g. Computer Equipment"
                  value={newAssetType.name}
                  onChange={(e) => setNewAssetType({...newAssetType, name: e.target.value})}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700">Asset account</label>
                <select 
                  required
                  className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                  value={newAssetType.assetAccount}
                  onChange={(e) => setNewAssetType({...newAssetType, assetAccount: e.target.value})}
                >
                  <option value="">Select account</option>
                  {accounts.filter(a => a.type === 'Fixed Asset').map(acc => (
                    <option key={acc.id} value={acc.code}>{acc.code} - {acc.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700">Accumulated depreciation account</label>
                <select 
                  required
                  className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                  value={newAssetType.accumulatedDepreciationAccount}
                  onChange={(e) => setNewAssetType({...newAssetType, accumulatedDepreciationAccount: e.target.value})}
                >
                  <option value="">Select account</option>
                  {accounts.filter(a => a.type === 'Fixed Asset').map(acc => (
                    <option key={acc.id} value={acc.code}>{acc.code} - {acc.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700">Depreciation account</label>
                <select 
                  required
                  className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                  value={newAssetType.depreciationAccount}
                  onChange={(e) => setNewAssetType({...newAssetType, depreciationAccount: e.target.value})}
                >
                  <option value="">Select account</option>
                  {accounts.filter(a => a.type === 'Expense').map(acc => (
                    <option key={acc.id} value={acc.code}>{acc.code} - {acc.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700">Depreciation method</label>
                  <select 
                    className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                    value={newAssetType.depreciationMethod}
                    onChange={(e) => setNewAssetType({...newAssetType, depreciationMethod: e.target.value})}
                  >
                    <option value="Straight Line">Straight Line</option>
                    <option value="Declining Balance">Declining Balance</option>
                    <option value="Full Depreciation">Full Depreciation</option>
                    <option value="None">No Depreciation</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700">Averaging method</label>
                  <select 
                    className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                    value={newAssetType.averagingMethod}
                    onChange={(e) => setNewAssetType({...newAssetType, averagingMethod: e.target.value})}
                  >
                    <option value="Full month">Full month</option>
                    <option value="The actual days">The actual days</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700">Basis</label>
                  <select 
                    className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                    value={newAssetType.calculateDepreciationBasedOn}
                    onChange={(e) => setNewAssetType({...newAssetType, calculateDepreciationBasedOn: e.target.value as any})}
                  >
                    <option value="Rate">Rate</option>
                    <option value="Effective Life">Effective life</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {newAssetType.calculateDepreciationBasedOn === 'Rate' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700">Depreciation rate (%)</label>
                    <input 
                      type="number" 
                      step="0.01"
                      className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                      value={newAssetType.depreciationRate || ''}
                      onChange={(e) => setNewAssetType({...newAssetType, depreciationRate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700">Effective life (Years)</label>
                    <input 
                      type="number" 
                      className="w-full border p-2 rounded outline-none focus:ring-1 focus:ring-blue-500"
                      value={newAssetType.effectiveLifeYears || ''}
                      onChange={(e) => setNewAssetType({...newAssetType, effectiveLifeYears: parseInt(e.target.value) || 0})}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-x-3 pt-6">
                <button 
                  type="button"
                  onClick={() => setIsAssetTypeModalOpen(false)}
                  className="px-6 py-2 rounded-lg font-semibold border bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isSaving}
                  className="bg-[#0073C0] text-white px-8 py-2 rounded-lg font-semibold hover:bg-[#005FA0] transition-colors flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>Save</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <ConfirmModal
          isOpen={!!deleteId}
          onClose={() => setDeleteId(null)}
          onConfirm={() => handleDeleteAsset(deleteId)}
          title="Delete Asset"
          message="Are you sure you want to delete this asset? This action cannot be undone."
        />
      )}
    </div>
  );
}
